'use client';

import Cookies from 'js-cookie';
import { ReadonlyURLSearchParams } from 'next/navigation';

import React, {
    ReactNode,
    createContext,
    memo,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';

import {
    GRS_REFINE_QUERY_PARAM,
    LW_REFINE_QUERY_PARAM,
} from '@/components/SearchResultsFacets/constants';
import {
    GRS_SEARCH_PROVIDER_QUERY_PARAM_VALUE,
    SEARCH_PROVIDER_QUERY_PARAM_KEY,
    SEARCH_SERVICE_PROVIDERS,
} from '@/constants/grs';
import { ADOBE_SESSION_ID_KEY } from '@/src/components/Adobe/constants';
import type {
    GRSFacetConfigProperties,
    GrsConfig,
    SearchPageConfigProperties,
    SearchServiceConfig,
} from '@/src/types/contentStack';
import { SearchParams } from '@/src/types/searchQuery';
import { PageType } from '@/types/pages/search';
import {
    getAdobeSessionId,
    getIsGRSSearchProviderSessionStorageSet,
    setGRSSearchProviderSessionStorage,
} from '@/utils/grs';
import {
    type ContentStackEntryDataProps,
    type LocaleProps,
    createUUID,
    logger,
    useBrowseContext,
    useLocalForage,
    useQueryParams,
    useSearchProvider,
} from '@nxt-costco-com/forge-digital-components';

import { DEFAULT_USER_STATE } from './constants';
import {
    GrsQuery,
    getAdTargetingFlags,
    isSearchResultGRS,
} from './services/Grs';
import {
    LucidWorksQuery,
    isSearchResultLW,
    isWAPOEnabledForWarehouse,
} from './services/LucidWorks';
import type {
    SearchContextType,
    SearchResult,
    WarehouseCookieValue,
} from './types';
import {
    checkForGrsItemNumberRedirect,
    checkForLucidWorksItemNumberRedirect,
    getLocationsFromCookie,
    getRequiredSearchParams,
    getUseZipCodeFromDeliveryLocations,
    getUserLocationFromDeliveryLocations,
    isBDSite,
    updatePageTitle,
} from './utils';

// Create the context with a default value
const SearchContext = createContext<SearchContextType | undefined>(undefined);

type SearchProviderProps = {
    lang: LocaleProps;
    config: ContentStackEntryDataProps<SearchPageConfigProperties>;
    serviceConfig: SearchPageConfigProperties | undefined;
    children: ReactNode;
    requestHeaders: SearchServiceConfig['required_request_headers'] | undefined;
    NoResultsComponent: ReactNode;
    loc?: string;
    whloc?: string;
    userZipCode?: string;
    userLocation?: string;
    pageType: PageType;
    isEnabled?: boolean;
    searchFiltersEnabled: boolean;
    enableLivePreview?: boolean;
    livePreviewKeyword?: string;
    mdo?: string;
    site: string;
    grsConfig: GrsConfig | undefined;
    grsFacetConfig:
        | ContentStackEntryDataProps<GRSFacetConfigProperties>
        | undefined;
    isGRSFeatureFlagEnabled: boolean;
    isGRSBetaReleaseFeatureFlagEnabled: boolean;
    isGRSPhasedReleaseFeatureFlagEnabled: boolean;
    categoryPageId: string | undefined;
    searchFsaExemptKeywords: string[] | undefined;
    categoryFsaFlag: boolean;
    isCategoryPage?: boolean;
    isWAPOEnabled?: boolean;
    wapoEnabledWarehouses?: string[];
    facetConfig?: ContentStackEntryDataProps<unknown> | undefined;
};

export const SearchProviderComponent = ({
    lang,
    config,
    serviceConfig,
    requestHeaders,
    children,
    NoResultsComponent,
    loc,
    whloc,
    userZipCode,
    userLocation = DEFAULT_USER_STATE,
    mdo,
    pageType,
    isEnabled = true,
    searchFiltersEnabled,
    enableLivePreview,
    livePreviewKeyword,
    site,
    grsConfig,
    grsFacetConfig,
    isGRSFeatureFlagEnabled,
    isGRSBetaReleaseFeatureFlagEnabled,
    isGRSPhasedReleaseFeatureFlagEnabled,
    categoryPageId,
    searchFsaExemptKeywords,
    categoryFsaFlag,
    // isCategoryPage,
    wapoEnabledWarehouses,
    isWAPOEnabled,
    facetConfig,
}: SearchProviderProps) => {
    const {
        isInitialized: isQueryParamsLoaded,
        queryParams: searchParams,
        setQueryParams,
    } = useQueryParams();

    const [searchResult, setSearchResult] = useState<
        SearchResult | undefined
    >();
    const [searchedParams, setSearchedParams] = useState<
        SearchParams | undefined
    >();
    const [isSearchResultsLoading, setIsSearchResultsLoading] =
        useState<boolean>(true);

    const searchService = useSearchProvider({
        isGRSFeatureFlagEnabled,
        isGRSBetaReleaseFeatureFlagEnabled,
        isGRSPhasedReleaseFeatureFlagEnabled,
    });

    const [sortType, setSortType] = useState<string>('');
    const {
        data: adobeSessionIdLocalForage,
        error: adobeLocalForageError,
        isStoreInitialized: isAdobeLocalForageInitialzed,
    } = useLocalForage<string>(ADOBE_SESSION_ID_KEY, createUUID());

    const adobeSessionID = getAdobeSessionId({
        searchService,
        adobeSessionIdLocalForage,
        hasLocalForageError: !!adobeLocalForageError,
        isLocalForageInitialzed: isAdobeLocalForageInitialzed,
    });

    const searchProviderQueryParam = searchParams.get(
        SEARCH_PROVIDER_QUERY_PARAM_KEY,
    );

    const isSearchProviderQueryParamGRS =
        searchProviderQueryParam?.toLowerCase() ===
        GRS_SEARCH_PROVIDER_QUERY_PARAM_VALUE;

    // Search is actively enabled, filters are enabled, or a refinement tag is present on a search disabled page.
    const hasRefinementParams =
        !!searchParams.get(LW_REFINE_QUERY_PARAM) ||
        !!searchParams.get(GRS_REFINE_QUERY_PARAM);
    const isSearchActive =
        isEnabled || (searchFiltersEnabled ?? false) || hasRefinementParams;

    // Set searchProvider session storage value for beta release
    useEffect(() => {
        const isGRSSearchProviderSessionStorageSet =
            getIsGRSSearchProviderSessionStorageSet();

        if (
            isSearchProviderQueryParamGRS &&
            !isGRSSearchProviderSessionStorageSet
        ) {
            setGRSSearchProviderSessionStorage();
        }
    }, [isSearchProviderQueryParamGRS]);

    useEffect(() => {
        const getEffectiveSearchParams = () => {
            // Needed to send to GRS request for personalization. User hashed Id present in cookies set by WCS.
            let effectiveSearchParams = searchParams;

            if (enableLivePreview && livePreviewKeyword) {
                const paramsObject = searchParams
                    ? Object.fromEntries(searchParams.entries())
                    : {};
                paramsObject['keyword'] = livePreviewKeyword;
                effectiveSearchParams = new URLSearchParams(
                    paramsObject,
                ) as ReadonlyURLSearchParams;
            }
            return effectiveSearchParams;
        };

        // Do not run search when search and filters are both disabled (merch pages) and no qualifying URL query params are present.
        if (!isSearchActive && isQueryParamsLoaded) {
            setIsSearchResultsLoading(false);
            const effectiveSearchParams = getEffectiveSearchParams();
            const adTargetingFlags = getAdTargetingFlags({
                searchParams: effectiveSearchParams,
                searchFsaExemptKeywords,
                categoryFsaFlag,
                includeEmptyPagination: true,
            });
            setSearchResult(adTargetingFlags as SearchResult);
            return;
        }
        const run = async () => {
            setIsSearchResultsLoading(true);
            try {
                const userInfoId = Cookies.get('costcoHashId');
                const effectiveSearchParams = getEffectiveSearchParams();

                const {
                    updatedSearchParams: requiredSearchParams,
                    filterChanged,
                } = getRequiredSearchParams(
                    searchService,
                    effectiveSearchParams,
                    grsFacetConfig,
                    facetConfig,
                );

                const requiredSearchParamString =
                    requiredSearchParams.toString();
                const effectiveSearchParamString =
                    effectiveSearchParams.toString();

                if (
                    requiredSearchParamString !== effectiveSearchParamString &&
                    !filterChanged
                ) {
                    window.history.replaceState(
                        {},
                        '',
                        `${window.location.pathname}?${requiredSearchParamString}`,
                    );
                    setQueryParams(
                        requiredSearchParams as ReadonlyURLSearchParams,
                    );
                } else {
                    let searchResult: SearchResult;
                    const isWAPOEnabledWarehouse = isWAPOEnabledForWarehouse(
                        wapoEnabledWarehouses,
                        whloc,
                        isWAPOEnabled,
                    );
                    switch (searchService) {
                        case SEARCH_SERVICE_PROVIDERS.LUCIDWORKS:
                            searchResult = await LucidWorksQuery(
                                effectiveSearchParams,
                                lang,
                                requestHeaders,
                                serviceConfig,
                                { loc, whloc, userLocation, mdo },
                                pageType,
                                site,
                            );
                            break;
                        case SEARCH_SERVICE_PROVIDERS.GRS:
                            searchResult = await GrsQuery(
                                grsConfig,
                                grsFacetConfig,
                                effectiveSearchParams,
                                lang,
                                // Use the page config (the same source the UI reads resultsPerPage
                                // from). The Lucidworks service config is optional and being
                                // retired, so it is only a fallback.
                                config?.properties?.[0] ?? serviceConfig,
                                {
                                    loc,
                                    whloc,
                                    userLocation,
                                    mdo,
                                    userZipCode,
                                },
                                pageType,
                                site,
                                adobeSessionID ?? '',
                                categoryPageId,
                                searchFsaExemptKeywords,
                                categoryFsaFlag,
                                userInfoId,
                                isWAPOEnabledWarehouse,
                                facetConfig,
                            );
                            break;
                        default:
                            throw new Error(
                                `Search service "${searchService}" not supported`,
                            );
                    }

                    const kw = effectiveSearchParams.get('keyword');
                    // checking for redirect based on GRS
                    const grsRedirect =
                        searchService === SEARCH_SERVICE_PROVIDERS.GRS &&
                        checkForGrsItemNumberRedirect(kw, searchResult);

                    const lucidWorksRedirect =
                        searchService === SEARCH_SERVICE_PROVIDERS.LUCIDWORKS &&
                        checkForLucidWorksItemNumberRedirect(kw, searchResult);

                    // if GRS is found then use GRS as priority else check for LucidWorks
                    if (grsRedirect) {
                        window.location.href = grsRedirect;
                        return;
                    }

                    if (lucidWorksRedirect) {
                        window.location.href = lucidWorksRedirect;
                        return;
                    }

                    if (searchResult?.redirect) {
                        /* Bug 114623: in case of category page redirect, onclick of back button was not navigating user to page it came from */
                        // replace method of location is used so that it doesn't save the current request in the browser history
                        window.location.replace(searchResult.redirect);

                        return;
                    }

                    updatePageTitle({
                        searchParams: effectiveSearchParams,
                        searchResult,
                        searchService,
                        grsFacetConfig,
                        facetConfig,
                    });
                    setSearchResult(searchResult);

                    setSearchedParams(
                        Object.fromEntries(effectiveSearchParams.entries()),
                    );
                }

                setIsSearchResultsLoading(false);
            } catch (err) {
                logger.error(`Error occurred: ${err}`);
                setIsSearchResultsLoading(false);
            }
        };

        // For BC, we need whloc to be set, for BD we don't use it
        const hasRequiredWHData = isBDSite(site) || (whloc && whloc !== '');

        // For GRS, we need additional data to avoid incomplete search requests
        const hasRequiredGRSData =
            searchService !== SEARCH_SERVICE_PROVIDERS.GRS ||
            (adobeSessionID &&
                (isBDSite(site) || whloc) &&
                (isBDSite(site) || userZipCode) &&
                loc &&
                (isBDSite(site) || loc !== '*'));

        // For LucidWorks, we need to ensure loc is fully initialized to avoid duplicate calls
        // BD sites use bdWarehouseNumber for loc, so wildcard check doesn't apply
        const hasRequiredLWData =
            searchService !== SEARCH_SERVICE_PROVIDERS.LUCIDWORKS ||
            (loc && (isBDSite(site) || loc !== '*'));

        if (
            loc &&
            (isBDSite(site) || userLocation) &&
            searchService &&
            hasRequiredWHData
        ) {
            if (
                searchService === SEARCH_SERVICE_PROVIDERS.GRS &&
                !hasRequiredGRSData
            ) {
                // Skip search run if required GRS data is not yet available
                logger.warn(`Skipping search run, awaiting initialization`);
            } else if (
                searchService === SEARCH_SERVICE_PROVIDERS.LUCIDWORKS &&
                !hasRequiredLWData
            ) {
                logger.warn(
                    `Skipping LucidWorks search run, awaiting complete initialization. loc is wildcard (BC sites only)`,
                );
            } else if (
                searchService === SEARCH_SERVICE_PROVIDERS.LUCIDWORKS &&
                isGRSFeatureFlagEnabled &&
                isGRSBetaReleaseFeatureFlagEnabled &&
                isSearchProviderQueryParamGRS
            ) {
                // Handle case where Lucidworks user switches to GRS but searchProvider has not yet updated
                logger.warn(
                    `Skipping search run, awaiting provider initialization`,
                );
            } else {
                if (isQueryParamsLoaded) {
                    run();
                }
            }
        } else {
            logger.warn(
                `Skipping search run, loc: ${loc} | ul: ${userLocation} | whloc: ${whloc}`,
            );
        }
    }, [
        adobeSessionID,
        categoryPageId,
        enableLivePreview,
        isGRSBetaReleaseFeatureFlagEnabled,
        isGRSFeatureFlagEnabled,
        isSearchProviderQueryParamGRS,
        pageType,
        lang,
        livePreviewKeyword,
        loc,
        requestHeaders,
        searchParams,
        searchService,
        config,
        serviceConfig,
        userLocation,
        whloc,
        site,
        grsConfig,
        grsFacetConfig,
        userZipCode,
        mdo,
        categoryFsaFlag,
        searchFsaExemptKeywords,
        isQueryParamsLoaded,
        setQueryParams,
        wapoEnabledWarehouses,
        isWAPOEnabled,
        facetConfig,
        isSearchActive,
    ]);

    const hasLWResults =
        searchService === SEARCH_SERVICE_PROVIDERS.LUCIDWORKS &&
        isSearchResultLW(searchResult) &&
        searchResult?.docs?.length > 0;

    const hasGRSResults =
        searchService === SEARCH_SERVICE_PROVIDERS.GRS &&
        isSearchResultGRS(searchResult) &&
        searchResult?.searchResult?.results?.length > 0;

    const isNoResultsComponentUsed =
        isSearchActive &&
        !isSearchResultsLoading &&
        (!searchResult || (!hasLWResults && !hasGRSResults));

    return (
        <SearchContext.Provider
            value={{
                searchResult,
                isSearchResultsLoading,
                searchedParams,
                searchService,
                sortType,
                setSortType,
            }}
        >
            {isNoResultsComponentUsed ? NoResultsComponent : children}
            <div
                dangerouslySetInnerHTML={{
                    __html: `<!--
                        resultsLeadToFSACHDI: ${searchResult?.isAdTargetingExplicitlyDisabled ? 1 : 0}
                        isFSACHDIExceptionSet: ${searchResult?.isAdTargetingExceptionEnabled ? 1 : 0}
                        doNotTrackLayout: ${!searchResult?.isAdTargetingEnabled}
                    -->`,
                }}
            />
        </SearchContext.Provider>
    );
};

const MemoizedSearchProviderComponent = memo(SearchProviderComponent);

// Create the provider component
export const SearchProvider = (props: SearchProviderProps) => {
    const {
        warehouse,
        deliveryLocation,
        bdDeliveryLocation,
        bdWarehouseNumber,
        onUserActivity,
    } = useBrowseContext();

    // Call onUserActivity whenever search params change (filter, sort, pagination).
    // Waits for queryParams to be initialized, captures the baseline string, then
    // only calls onUserActivity() when the params actually change — not during the
    // initial initialization transition or on re-renders with identical params.
    const { isInitialized: isQueryParamsLoaded, queryParams } =
        useQueryParams();
    const prevQueryString = useRef<string | null>(null);
    useEffect(() => {
        if (!isQueryParamsLoaded || !queryParams) return;
        const nextQueryString = queryParams.toString();
        if (prevQueryString.current === null) {
            prevQueryString.current = nextQueryString;
            return;
        }
        if (prevQueryString.current === nextQueryString) return;
        prevQueryString.current = nextQueryString;
        onUserActivity?.();
    }, [isQueryParamsLoaded, queryParams, onUserActivity]);

    // TODO: Use this from context over deriving it manually like this.
    const { mdo } = getLocationsFromCookie(warehouse as WarehouseCookieValue);
    let { loc, whloc } = getLocationsFromCookie(
        warehouse as WarehouseCookieValue,
    );
    const userLocation = getUserLocationFromDeliveryLocations({
        deliveryLocation,
        bdDeliveryLocation,
        site: props.site,
        lang: props.lang,
    });
    const userZipCode = getUseZipCodeFromDeliveryLocations({
        deliveryLocation,
        bdDeliveryLocation,
        site: props.site,
    });
    // BD does not use the whloc param, and uses the BD warehouse cookie for loc instead
    if (isBDSite(props.site)) {
        whloc = '';
        loc =
            bdWarehouseNumber?.value ||
            (props?.serviceConfig?.defaultBDWarehouse as string);
    }

    return (
        <MemoizedSearchProviderComponent
            loc={loc}
            whloc={whloc}
            userLocation={userLocation}
            userZipCode={userZipCode}
            mdo={mdo}
            {...props}
        />
    );
};

// Create a custom hook to use the SearchContext
export const useSearchContext = (): SearchContextType => {
    const context = useContext(SearchContext);
    if (context === undefined) {
        throw new Error(
            'useSearchContext must be used within a SearchProvider',
        );
    }
    return context;
};
